import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BrandHomeLink } from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== false) return <Navigate to="/vault" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const r = await login(email, password);
    setSubmitting(false);
    if (r.ok) {
      if (r.user?.must_change_password) toast.info("Please set a new password to continue");
      else toast.success("Welcome back");
      nav("/vault");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen flex relative" style={{ background: "var(--vault-app-bg)" }}>
      <ThemeToggle className="absolute top-5 right-5 z-10" testid="login-theme-toggle" />
      {/* Visual side */}
      <div className="hidden lg:flex w-1/2 flex-col justify-between p-12" style={{ background: "var(--vault-sidebar-bg)" }}>
        <BrandHomeLink size="lg" testid="login-side-brand-logo" linkTestid="login-brand-home-link" />
        <div>
          <img
            src="https://static.prod-images.emergentagent.com/jobs/649c5d98-ab9a-4383-a42c-cabe200e9bef/images/ab77f75dca5c8795f454531ad9c4f1466856e9cbb476ca51871f5b4a26f7806c.png"
            alt="Secure vault"
            className="w-full max-w-md mx-auto"
          />
        </div>
        <div className="max-w-md">
          <h2 className="font-[Outfit] text-3xl tracking-tight mb-3">A calm place for the keys to your life.</h2>
          <p className="text-[color:var(--vault-text-secondary)] text-sm leading-relaxed">
            Store passwords for you and your family. Share specific accounts with people you trust, and
            designate trusted contacts who can reach your vault if something happens.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <img
              src="/ezguard-logo-full.webp"
              alt="EZGuard"
              className="hero-logo w-44 sm:w-52 h-auto mx-auto mb-8 select-none"
              draggable={false}
              data-testid="login-hero-logo"
            />
            <h1 className="font-[Outfit] text-4xl tracking-tight font-semibold">Welcome back</h1>
            <p className="text-[color:var(--vault-text-secondary)] text-sm mt-2">Sign in to unlock your vault.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Master password</Label>
              </div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1 font-mono"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
              data-testid="login-submit-btn"
            >
              <KeyRound size={16} className="mr-2" />
              {submitting ? "Unlocking…" : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-[color:var(--vault-text-tertiary)] mt-6">
            Don&apos;t have a vault?{" "}
            <Link to="/register" className="text-[color:var(--vault-brand)] font-medium hover:underline" data-testid="link-to-register">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
